import { useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { CalendarDays, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";

const CAL = { caption_label: "hidden", nav: "hidden", caption_dropdowns: "flex gap-2", dropdown: "c-input !h-8 !py-0 !px-2 text-xs", vhidden: "hidden" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const POP = "w-auto p-0 bg-[#121824] border-[#1F2A3E] text-slate-100";
const parse = (v) => { const d = v ? parseISO(v) : null; return d && isValid(d) ? d : null; };

// Keeps native "required" form validation working for the button-based pickers.
const Req = ({ value }) => <input tabIndex={-1} aria-hidden required value={value || ""} onChange={() => {}} className="absolute inset-x-0 bottom-0 h-px w-full opacity-0 pointer-events-none" />;

const Trigger = ({ text, testid, required, clear }) => (
  <PopoverTrigger asChild>
    <button type="button" className={`c-input flex items-center justify-between text-left ${clear ? "pr-16" : "pr-10"}`} data-testid={testid} aria-required={required}>
      <span className={text ? "text-slate-100" : "text-slate-500"}>{text || "Pick a date"}</span>
      <CalendarDays size={15} className="text-[#00E599] absolute right-3" />
    </button>
  </PopoverTrigger>);

export const DatePicker = ({ value, onChange, testid, fromYear = 1950, toYear = new Date().getFullYear() + 15, max, required, clearable = true, defaultMonth, className = "" }) => {
  const [open, setOpen] = useState(false);
  const val = parse(value), maxD = parse(max);
  return (
    <div className={`relative ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <Trigger text={val && format(val, "d MMM yyyy")} testid={testid} required={required} clear={clearable && val} />
        <PopoverContent className={POP} align="start" data-testid={`${testid}-calendar`}>
          <Calendar mode="single" selected={val || undefined} defaultMonth={val || defaultMonth || maxD || new Date()} captionLayout="dropdown-buttons"
            fromYear={fromYear} toYear={maxD ? Math.min(toYear, maxD.getFullYear()) : toYear} disabled={maxD ? { after: maxD } : undefined} classNames={CAL}
            onSelect={(x) => { if (x) { onChange(format(x, "yyyy-MM-dd")); setOpen(false); } }} />
        </PopoverContent>
      </Popover>
      {clearable && val && <button type="button" aria-label="Clear date" onClick={() => onChange("")} className="absolute right-9 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-white" data-testid={`${testid}-clear`}><X size={13} /></button>}
      {required && <Req value={value} />}
    </div>);
};

export const DateField = ({ label, k, f, set, tick, fromYear = 1950, toYear = new Date().getFullYear() + 1, required }) => (
  <div className="grid gap-1.5">
    <span className="text-xs text-slate-400 flex items-center gap-1.5">{label}{tick}</span>
    <DatePicker value={f[k]} onChange={(v) => set(k, v)} testid={`wiz-${k}`} fromYear={fromYear} toYear={toYear} required={required}
      defaultMonth={k === "dob" ? new Date(1995, 0, 1) : undefined} />
  </div>);

export const MonthPicker = ({ value, onChange, testid, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [y, m] = (value || "").split("-").map(Number);
  const [year, setYear] = useState(y || new Date().getFullYear());
  const nav = (d, l, Icon) => <button type="button" aria-label={l} onClick={() => setYear(year + d)} className="h-8 w-8 grid place-items-center rounded-md border border-[#1F2A3E] hover:bg-[#1A2234]" data-testid={`${testid}-${d < 0 ? "prev" : "next"}-year`}><Icon size={14} /></button>;
  return (
    <div className={`relative ${className}`}>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && y) setYear(y); }}>
        <PopoverTrigger asChild>
          <button type="button" className="c-input flex items-center justify-between text-left pr-10 font-mono min-w-[10.5rem]" data-testid={testid}>
            <span className={y ? "text-slate-100" : "text-slate-500"}>{y && m ? `${MONTHS[m - 1]} ${y}` : "Pick a month"}</span>
            <CalendarDays size={15} className="text-[#00E599] absolute right-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent className={`${POP} p-3 w-64`} align="start" data-testid={`${testid}-calendar`}>
          <div className="flex items-center justify-between mb-3">{nav(-1, "Previous year", ChevronLeft)}
            <span className="font-mono text-sm" data-testid={`${testid}-year`}>{year}</span>{nav(1, "Next year", ChevronRight)}</div>
          <div className="grid grid-cols-3 gap-1.5">
            {MONTHS.map((l, i) => {
              const on = year === y && i + 1 === m;
              return <button key={l} type="button" data-testid={`${testid}-m-${i + 1}`} onClick={() => { onChange(`${year}-${String(i + 1).padStart(2, "0")}`); setOpen(false); }}
                className={`rounded-md py-2 text-xs transition-colors ${on ? "bg-[#00E599] text-[#0B1017] font-semibold" : "hover:bg-[#1A2234] text-slate-300"}`}>{l}</button>;
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>);
};
